import type { CachingDataSourceQuery } from './caching-data-source-query';

export interface RequestablePage {
  isPageRequested(pageNumber: number): boolean;
}

export interface ImmutablePaginatedCache<T, Q extends CachingDataSourceQuery> extends RequestablePage {
  isPageCached(pageNumber: number): boolean;

  getRows(): Array<T>;

  reset(query: Q, offset: number, totalRecords: number | undefined, records: Array<T>): void;

  addRows(query: Q, offset: number, totalRecords: number | undefined, records: Array<T>): void;

  markRequestedPages(query: Q): void;
}

export class ArrayImmutablePaginatedCache<T, Q extends CachingDataSourceQuery> implements ImmutablePaginatedCache<T, Q> {

  private rows: Array<T> = [];
  private cachedPages: Array<boolean> = [];
  private requestedPages: Array<boolean> = [];

  constructor() {
  }

  getRows(): Array<T> {
    return this.rows;
  }

  isPageCached(pageNumber: number): boolean {
    return this.cachedPages[pageNumber] === true;
  }

  isPageRequested(pageNumber: number): boolean {
    return this.requestedPages[pageNumber] === true;
  }

  reset(query: Q, offset: number, totalRecords: number, records: Array<T>): void {
    this.rows = new Array<T>(totalRecords);
    this.rows.splice(offset, records.length, ...records);
    this.cachedPages = [];
    this.markCachedPages(offset, query.getPage().getPageSize(), records);
    this.requestedPages = this.cachedPages.slice(0);
  }

  addRows(query: Q, offset: number, _totalRecords: number, records: Array<T>): void {
    const rows = [...this.rows];
    rows.splice(offset, records.length, ...records);
    this.rows = rows;
    this.markCachedPages(offset, query.getPage().getPageSize(), records);
  }

  markRequestedPages(query: Q): void {
    let start = Math.ceil(query.getSearchOffset() / query.getPage().getPageSize());
    const finish = start + Math.ceil(query.getSearchSize() / query.getPage().getPageSize());
    while (start < finish) {
      this.requestedPages[start++] = true;
    }
  }

  private markCachedPages(offset: number, pageSize: number, records: Array<unknown>): void {
    let startPageNumber = Math.ceil(offset / pageSize);
    const endPageNumber = startPageNumber + Math.floor((records.length - 1) / pageSize);
    const reminder = offset - Math.floor(offset / pageSize) * pageSize;
    startPageNumber = reminder === 0 ? startPageNumber : startPageNumber + 1;
    if (endPageNumber >= 0) {
      while (startPageNumber <= endPageNumber) {
        this.cachedPages[startPageNumber++] = true;
      }
    }
  }
}
